// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Three routes and one menu entry. There is no «my tasks» route of its own:
// `/tasks` already filters by person, and a second screen showing the same rows
// with one filter pre-set is a second screen to keep in step with the first.

import { withAnalytics } from './i18n/analytics.js'

export const routes = [
  {
    path: '/tasks',
    name: 'tasks',
    component: () => import('./pages/TasksPage.vue'),
    meta: { permission: 'tasks:read', title: 'tasks.title' },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but new sits first anyway, for the reader.
  {
    path: '/tasks/new',
    name: 'task-new',
    component: () => import('./pages/TaskFormPage.vue'),
    meta: { permission: 'tasks:write', title: 'tasks.newTitle' },
  },
  {
    path: '/tasks/:id',
    name: 'task',
    component: () => import('./pages/TaskPage.vue'),
    // `complete` as well, so somebody who may only tick tasks off can still open
    // one from the file of the place they are standing in.
    meta: { permission: ['tasks:read', 'tasks:complete'], title: 'tasks.detailTitle' },
  },
  {
    path: '/tasks/:id/edit',
    name: 'task-edit',
    component: () => import('./pages/TaskFormPage.vue'),
    meta: { permission: 'tasks:write', title: 'tasks.editTitle' },
  },
]

export const navigation = [
  {
    to: '/tasks',
    labelKey: 'tasks.title',
    permission: 'tasks:read',
    icon: 'checklist',
    group: 'business',
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16), including what this module is called on the PLATFORM's own
// screens — Roles, Settings, Audit and the assistant panel — because a module
// that makes somebody add keys to the shell is not extractable.
const baseMessages = {
  es: {
    tasks: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'una tarea tuya vencida | {count} tareas tuyas vencidas',
        open: 'una tarea pendiente | {count} tareas pendientes',
      },
      assistant: {
        // Las respuestas que contestan las reglas (queries.js) y la pregunta de
        // «completar una tarea» por voz (intents.js).
        query: {
          mine: {
            some: 'Tienes {count} tareas abiertas: {list}.',
            none: 'No tienes ninguna tarea abierta.',
          },
        },
        complete: {
          askTask: '¿Qué tarea?',
          action: 'Dar por hecha una tarea',
          field: { task: 'Tarea', assignee: 'Era para', note: 'Cómo ha ido' },
          where: 'En',
          after: 'Se cierra a tu nombre, con la fecha de hoy',
          notes: {
            noTask: '¿Qué tarea? Di su código o lo que pone',
            report: 'Esta se cierra rellenando su parte, no marcándola',
          },
          outcome: 'Tarea {code} hecha',
        },
        create: {
          summary: {
            plain: 'Nueva tarea: {title}',
            at: 'Nueva tarea en {subject}: {title}',
            for: 'Nueva tarea para {assignee}: {title}',
            atfor: 'Nueva tarea en {subject} para {assignee}: {title}',
          },
          action: 'Crear tarea',
          field: {
            title: 'Qué hay que hacer',
            description: 'Detalle',
            assignee: 'Para',
            due: 'Para cuándo',
            priority: 'Prioridad',
          },
          where: 'Tarea en',
          outcome: 'Tarea {code} creada',
        },
      },
      title: 'Tareas',
      // Dos avisos y dos interruptores: uno baja al campo y el otro sube
      // a la oficina, y quien quiera callar el segundo no tiene por qué
      // perder el primero.
      notifications: {
        categories: {
          assigned: 'Te asignan una tarea',
          completed: 'Hacen una tarea que pediste',
        },
        assigned: {
          title: 'Tarea nueva para ti',
          body: '{code} · {title}{where}',
        },
        completed: {
          title: 'Tarea hecha',
          body: '{code} · {title}',
        },
      },
      subtitle: 'Lo que hay apuntado y todavía no ha hecho nadie',

      // El título y el resumen del tema de ayuda. Van aquí y no dentro del
      // `help/es.md` porque el índice de la documentación los necesita antes de
      // abrir nada, y cargar veinticinco módulos por cuatro idiomas para
      // dibujar una lista serían cientos de kilobytes en el arranque.
      help: {
        title: 'Tareas',
        subtitle: 'Las dos maneras de cerrar una tarea, por qué una tarea no vence nunca, y dónde sale lo pendiente de cada sitio',
      },
      detailTitle: 'Tarea',
      newTitle: 'Nueva tarea',
      editTitle: 'Editar la tarea',
      late: 'Fuera de fecha',

      related: {
        // Lo que se lee en la pestaña de la ficha de un sitio, una máquina o
        // una obra. «Pendiente» y no «Tareas»: en la ficha de un jardín la
        // pregunta no es qué tareas existen, es qué queda por hacer ahí.
        pending: 'Pendiente aquí',
      },

      actions: {
        new: 'Nueva tarea',
        complete: 'Dar por hecha',
        skip: 'No se ha podido',
        edit: 'Editar',
        cancel: 'Anular',
        reopen: 'Reabrir',
      },

      status: {
        pending: 'Pendiente',
        in_progress: 'En curso',
        done: 'Hecha',
        skipped: 'No se pudo',
        cancelled: 'Anulada',
      },

      priority: { low: 'Baja', medium: 'Normal', high: 'Alta', critical: 'Crítica' },

      subjectType: {
        property: 'Emplazamiento',
        asset: 'Activo',
        project: 'Proyecto',
        vehicle: 'Vehículo',
        customer: 'Cliente',
        location: 'Ubicación',
        work_order: 'Orden de trabajo',
        work: 'Obra',
      },

      stats: {
        open: 'Pendientes',
        overdue: 'Fuera de fecha',
        skipped: 'No se pudieron',
        mine: 'Mías',
      },

      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Lo que pone, el código o dónde es',
        status: 'Estado',
        statusAny: 'Cualquiera',
        type: 'Tipo',
        typeAny: 'Cualquiera',
        priority: 'Prioridad',
        priorityAny: 'Cualquiera',
      },

      columns: {
        code: 'Código',
        task: 'Tarea',
        status: 'Estado',
        priority: 'Prioridad',
        waiting: 'Esperando',
      },

      waiting: {
        today: 'Hoy',
        days: 'Hace {days} días',
        months: 'Hace {months} meses',
      },

      empty: {
        title: 'No hay nada apuntado',
        hint: 'Apunta lo que veas y se quedará aquí hasta que alguien lo haga.',
      },

      // Tachar varias de una vez: lo que hace de verdad alguien que termina
      // en un sitio con seis cosas hechas en la cabeza.
      bulk: {
        title: 'Lo que has marcado',
        chosen: '{n} marcadas | {n} marcada | {n} marcadas',
        clear: 'Quitar la marca',
        complete: 'Hecho ({n})',
        done: 'Se han dado por hechas {n}',
        failed: 'No se ha podido con {n}: siguen marcadas',
      },

      pager: { previous: 'Anterior', next: 'Siguiente', page: 'Página {page} de {of}' },

      outcome: {
        done: 'Hecha por {who} el {when}',
        skipped: 'No se pudo el {when}',
        cancelled: 'Anulada el {when}',
        somebody: 'alguien',
      },

      materials: {
        title: 'Qué hay que llevarse',
        noQuantity: 'sin cantidad',
        note: 'Esto es lo que hace falta, no lo gastado: no mueve el almacén. Lo gastado se apunta en el parte de la orden.',
      },

      files: {
        title: 'Fotos y papeles',
        empty: 'Nada adjunto todavía',
        add: 'Adjuntar un fichero',
        uploading: 'Subiendo…',
        remove: 'Quitar',
      },

      facts: {
        title: 'La ficha',
        subject: 'Dónde',
        assignee: 'De quién es',
        nobody: 'De nadie todavía',
        due: 'Para cuándo',
        noDue: 'Sin fecha',
        written: 'Apuntada el',
        origin: 'Origen',
        importedFrom: 'Importada de {system}',
        writtenThere: 'creada allí el {date}',
      },

      history: {
        title: 'Historial',
        created: 'Apuntada',
        updated: 'Modificada',
        note: 'Nota',
        add: 'Añadir',
        addPlaceholder: 'Qué ha pasado…',
      },

      skip: {
        title: 'No se ha podido hacer',
        reason: 'Por qué',
        hint: 'Esta frase es lo único que distingue «fuimos y no se pudo» de «no ha ido nadie».',
        placeholder: 'La puerta estaba cerrada…',
        cancel: 'Dejarlo',
        confirm: 'Apuntarlo',
      },

      confirm: {
        complete: '¿La das por hecha? Quedará con tu nombre y la fecha de hoy.',
        cancelTitle: 'Anular la tarea',
        cancelMessage: 'Deja de estar pendiente y no se borra: sigue en el historial con quien la apuntó.',
        detachTitle: 'Quitar el fichero',
        detachMessage: '«{name}» dejará de estar en esta tarea.',
      },

      toast: {
        created: 'Tarea apuntada',
        saved: 'Tarea guardada',
        completed: 'Dada por hecha',
        skipped: 'Apuntado que no se pudo',
        reopened: 'Vuelve a estar pendiente',
        cancelled: 'Tarea anulada',
        attached: 'Adjuntado',
      },

      dictate: {
        title: 'Dictarla',
        subtitle: 'Grábala y se rellena sola. Luego la repasas y la guardas.',
        clip: 'La nota de voz',
        read: 'Rellenar con lo que he dicho',
        heard: 'Lo que se ha oído',
        silence: 'No se ha oído nada. Vuelve a grabarla, más cerca de la boca.',
        which: '«{words}»: hay varios. ¿Cuál es?',
        notFound: 'No he encontrado «{words}». Escógelo abajo a mano.',
        keeps: 'La grabación se queda colgada de la tarea al guardarla. Quítala con la × si no la quieres.',
        clipFailed: 'La tarea se ha guardado, pero la grabación no se ha podido colgar.',
      },

      // Las dos maneras de cerrar una tarea, dichas con las palabras de quien
      // la lee. Ni «con reporte» ni «sin reporte» a secas: lo que le importa a
      // quien mira la ficha es si puede darla por hecha ahora o no.
      report: {
        withReport: 'Con parte',
        withoutReport: 'Sin parte',
        closedByReport: 'Esta tarea se cierra rellenando un parte de trabajo, no marcándola.',
        closedByReportAt: 'Esta tarea se cierra rellenando el parte de {place}, no marcándola.',
      },
      form: {
        subtitle: 'Qué hay que hacer, y dónde o a quién le toca',
        requiresReport: 'Cómo se cierra',
        requiresReportOn: 'Se cerrará rellenando un parte en la orden de trabajo del sitio. No se podrá marcar desde aquí.',
        requiresReportOff: 'Se cierra marcándola, con una nota si hace falta. Sus horas y su material no quedan apuntados en ningún sitio.',
        what: 'Qué hay que hacer',
        who: 'Dónde y de quién',
        title: 'La tarea',
        titleHint: 'Una frase. La que dirías por teléfono.',
        titlePlaceholder: 'Cambiar el aspersor de la zona 3',
        description: 'Detalles',
        type: 'Tipo',
        typeAny: 'Sin tipo',
        priority: 'Prioridad',
        subjectType: 'Sobre qué',
        subjectNone: 'Sobre nada en concreto',
        subject: 'Cuál',
        subjectPlaceholder: 'Busca el sitio, la máquina o la obra',
        assignee: 'A quién le toca',
        assigneeHint: 'Puede quedarse sin nadie: entonces espera a quien pase por allí.',
        assigneePlaceholder: 'Busca a la persona',
        due: 'Para cuándo',
        dueHint: 'Casi siempre vacío. Una tarea de un sitio espera, no vence.',
        unreachable: 'Ponle un sitio o una persona: si no, no aparecería en ninguna pantalla.',
        save: 'Guardar',
        discard: 'Descartar',
        materials: 'Qué hay que llevarse',
        materialsHint: 'Lo que hace falta para hacerla. No descuenta del almacén.',
        materialPlaceholder: 'Busca en el almacén o escríbelo',
        quantity: 'Cantidad',
        unit: 'Unidad',
        removeLine: 'Quitar la línea',
        noMaterials: 'Nada apuntado. Si hace falta material, ponlo aquí y quien vaya lo verá antes de salir.',
        addMaterial: 'Añadir material',
        leaveTitle: 'Te dejas la tarea a medias',
        leaveMessage: 'Lo que has escrito no se ha guardado.',
        leaveConfirm: 'Salir igual',
        leaveCancel: 'Seguir aquí',
      },
    },

    assistant: {
      openers: {
        tasks: {
          'pending-here': '¿Qué hay pendiente en un sitio?',
          'what-mine': '¿Qué tengo yo pendiente?',
        },
      },
    },

    modules: { tasks: 'Tareas' },
    permissions: {
      tasks: {
        read: 'Ver las tareas y lo que hay pendiente en cada sitio',
        complete: 'Dar tareas por hechas y apuntar que no se pudo',
        write: 'Apuntar tareas, editarlas y anularlas',
        import: 'Importar tareas de otro programa',
      },
    },

    settings: {
      ns: {
        tasks: {
          title: 'Tareas',
          subtitle: 'Cómo llamáis a lo que hacéis, si es que le ponéis nombre',
          alwaysRequireReport: {
            label: 'Las tareas nuevas se cierran con un parte',
            help: 'Encendido, cada tarea nueva nace esperando que alguien rellene un parte — que es como quedan apuntadas sus horas y su material. Apagado, se cierra con un tick. En los dos casos se puede cambiar en la propia tarea.',
          },
          types: {
            label: 'Los tipos de tarea que hacéis',
            help: 'Vuestros, no nuestros, y opcional: si lo dejas vacío una tarea es la frase que escribió alguien.',
          },
        },
      },
    },

    audit: {
      actions: {
        tasks: {
          task_created: 'Tarea apuntada',
          task_updated: 'Tarea modificada',
          task_done: 'Tarea dada por hecha',
          task_skipped: 'Tarea que no se pudo hacer',
          task_cancelled: 'Tarea anulada',
          task_pending: 'Tarea reabierta',
          task_in_progress: 'Tarea empezada',
          task_noted: 'Nota en una tarea',
          task_attached: 'Fichero adjuntado a una tarea',
          task_detached: 'Fichero quitado de una tarea',
          imported: 'Tarea importada de otro programa',
        },
      },
    },
  },

  ca: {
    tasks: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'una tasca teva vençuda | {count} tasques teves vençudes',
        open: 'una tasca pendent | {count} tasques pendents',
      },
      assistant: {
        // Las respuestas que contestan las reglas (queries.js) y la pregunta de
        // «completar una tarea» por voz (intents.js).
        query: {
          mine: {
            some: 'Tens {count} tasques obertes: {list}.',
            none: 'No tens cap tasca oberta.',
          },
        },
        complete: {
          askTask: 'Quina tasca?',
          action: 'Donar per feta una tasca',
          field: { task: 'Tasca', assignee: 'Era per a', note: 'Com ha anat' },
          where: 'A',
          after: 'Es tanca al teu nom, amb la data d\'avui',
          notes: {
            noTask: 'Quina tasca? Digues el codi o el que hi posa',
            report: 'Aquesta es tanca omplint el seu part, no marcant-la',
          },
          outcome: 'Tasca {code} feta',
        },
        create: {
          summary: {
            plain: 'Nova tasca: {title}',
            at: 'Nova tasca a {subject}: {title}',
            for: 'Nova tasca per a {assignee}: {title}',
            atfor: 'Nova tasca a {subject} per a {assignee}: {title}',
          },
          action: 'Crear tasca',
          field: {
            title: 'Què cal fer',
            description: 'Detall',
            assignee: 'Per a',
            due: 'Per a quan',
            priority: 'Prioritat',
          },
          where: 'Tasca a',
          outcome: 'Tasca {code} creada',
        },
      },
      title: 'Tasques',
      notifications: {
        categories: {
          assigned: 'T\'assignen una tasca',
          completed: 'Fan una tasca que vas demanar',
        },
        assigned: {
          title: 'Tasca nova per a tu',
          body: '{code} · {title}{where}',
        },
        completed: {
          title: 'Tasca feta',
          body: '{code} · {title}',
        },
      },
      subtitle: 'El que hi ha apuntat i encara no ha fet ningú',

      help: {
        title: 'Tasques',
        subtitle: 'Les dues maneres de tancar una tasca, per què una tasca no venç mai, i on surt el pendent de cada lloc',
      },
      detailTitle: 'Tasca',
      newTitle: 'Nova tasca',
      editTitle: 'Editar la tasca',
      late: 'Fora de data',

      related: { pending: 'Pendent aquí' },

      actions: {
        new: 'Nova tasca',
        complete: 'Donar per feta',
        skip: 'No s\'ha pogut',
        edit: 'Editar',
        cancel: 'Anul·lar',
        reopen: 'Reobrir',
      },

      status: {
        pending: 'Pendent',
        in_progress: 'En curs',
        done: 'Feta',
        skipped: 'No es va poder',
        cancelled: 'Anul·lada',
      },

      priority: { low: 'Baixa', medium: 'Normal', high: 'Alta', critical: 'Crítica' },

      subjectType: {
        property: 'Emplaçament',
        asset: 'Actiu',
        project: 'Projecte',
        vehicle: 'Vehicle',
        customer: 'Client',
        location: 'Ubicació',
        work_order: 'Ordre de treball',
        work: 'Obra',
      },

      stats: {
        open: 'Pendents',
        overdue: 'Fora de data',
        skipped: 'No es van poder',
        mine: 'Meves',
      },

      filters: {
        search: 'Cercar',
        searchPlaceholder: 'El que hi diu, el codi o on és',
        status: 'Estat',
        statusAny: 'Qualsevol',
        type: 'Tipus',
        typeAny: 'Qualsevol',
        priority: 'Prioritat',
        priorityAny: 'Qualsevol',
      },

      columns: {
        code: 'Codi',
        task: 'Tasca',
        status: 'Estat',
        priority: 'Prioritat',
        waiting: 'Esperant',
      },

      waiting: {
        today: 'Avui',
        days: 'Fa {days} dies',
        months: 'Fa {months} mesos',
      },

      empty: {
        title: 'No hi ha res apuntat',
        hint: 'Apunta el que vegis i es quedarà aquí fins que algú ho faci.',
      },

      bulk: {
        title: 'El que has marcat',
        chosen: '{n} marcades | {n} marcada | {n} marcades',
        clear: 'Treure la marca',
        complete: 'Fet ({n})',
        done: 'S\u2019han donat per fetes {n}',
        failed: 'No s\u2019ha pogut amb {n}: segueixen marcades',
      },

      pager: { previous: 'Anterior', next: 'Següent', page: 'Pàgina {page} de {of}' },

      outcome: {
        done: 'Feta per {who} el {when}',
        skipped: 'No es va poder el {when}',
        cancelled: 'Anul·lada el {when}',
        somebody: 'algú',
      },

      materials: {
        title: 'Què cal endur-se',
        noQuantity: 'sense quantitat',
        note: 'Això és el que fa falta, no el que s\'ha gastat: no mou el magatzem. El que s\'ha gastat s\'apunta al comunicat de l\'ordre.',
      },

      files: {
        title: 'Fotos i papers',
        empty: 'Encara no hi ha res adjunt',
        add: 'Adjuntar un fitxer',
        uploading: 'Pujant…',
        remove: 'Treure',
      },

      facts: {
        title: 'La fitxa',
        subject: 'On',
        assignee: 'De qui és',
        nobody: 'De ningú encara',
        due: 'Per quan',
        noDue: 'Sense data',
        written: 'Apuntada el',
        origin: 'Origen',
        importedFrom: 'Importada de {system}',
        writtenThere: 'creada allà el {date}',
      },

      history: {
        title: 'Historial',
        created: 'Apuntada',
        updated: 'Modificada',
        note: 'Nota',
        add: 'Afegir',
        addPlaceholder: 'Què ha passat…',
      },

      skip: {
        title: 'No s\'ha pogut fer',
        reason: 'Per què',
        hint: 'Aquesta frase és l\'única cosa que distingeix «hi vam anar i no es va poder» de «no hi ha anat ningú».',
        placeholder: 'La porta era tancada…',
        cancel: 'Deixar-ho',
        confirm: 'Apuntar-ho',
      },

      confirm: {
        complete: 'La dones per feta? Quedarà amb el teu nom i la data d\'avui.',
        cancelTitle: 'Anul·lar la tasca',
        cancelMessage: 'Deixa d\'estar pendent i no s\'esborra: continua a l\'historial amb qui la va apuntar.',
        detachTitle: 'Treure el fitxer',
        detachMessage: '«{name}» deixarà d\'estar en aquesta tasca.',
      },

      toast: {
        created: 'Tasca apuntada',
        saved: 'Tasca desada',
        completed: 'Donada per feta',
        skipped: 'Apuntat que no es va poder',
        reopened: 'Torna a estar pendent',
        cancelled: 'Tasca anul·lada',
        attached: 'Adjuntat',
      },

      dictate: {
        title: 'Dictar-la',
        subtitle: 'Grava-la i s\'omple sola. Després la repasses i la deses.',
        clip: 'La nota de veu',
        read: 'Omplir amb el que he dit',
        heard: 'El que s\'ha sentit',
        silence: 'No s\'ha sentit res. Torna a gravar-la, més a prop de la boca.',
        which: '«{words}»: n\'hi ha uns quants. Quin és?',
        notFound: 'No he trobat «{words}». Tria\'l a baix a mà.',
        keeps: 'La gravació queda penjada de la tasca en desar-la. Treu-la amb la × si no la vols.',
        clipFailed: 'La tasca s\'ha desat, però la gravació no s\'ha pogut penjar.',
      },

      report: {
        withReport: 'Amb comunicat',
        withoutReport: 'Sense comunicat',
        closedByReport: 'Aquesta tasca es tanca omplint un comunicat de treball, no marcant-la.',
        closedByReportAt: 'Aquesta tasca es tanca omplint el comunicat de {place}, no marcant-la.',
      },
      form: {
        subtitle: 'Què s\'ha de fer, i on o a qui li toca',
        requiresReport: 'Com es tanca',
        requiresReportOn: 'Es tancarà omplint un comunicat a l’ordre de treball del lloc. No es podrà marcar des d’aquí.',
        requiresReportOff: 'Es tanca marcant-la, amb una nota si cal. Les seves hores i el seu material no queden apuntats enlloc.',
        what: 'Què s\'ha de fer',
        who: 'On i de qui',
        title: 'La tasca',
        titleHint: 'Una frase. La que diries per telèfon.',
        titlePlaceholder: 'Canviar l\'aspersor de la zona 3',
        description: 'Detalls',
        type: 'Tipus',
        typeAny: 'Sense tipus',
        priority: 'Prioritat',
        subjectType: 'Sobre què',
        subjectNone: 'Sobre res en concret',
        subject: 'Quin',
        subjectPlaceholder: 'Busca el lloc, la màquina o l\'obra',
        assignee: 'A qui li toca',
        assigneeHint: 'Pot quedar-se sense ningú: llavors espera qui hi passi.',
        assigneePlaceholder: 'Busca la persona',
        due: 'Per quan',
        dueHint: 'Gairebé sempre buit. Una tasca d\'un lloc espera, no venç.',
        unreachable: 'Posa-li un lloc o una persona: si no, no apareixeria a cap pantalla.',
        save: 'Desar',
        discard: 'Descartar',
        materials: 'Què cal endur-se',
        materialsHint: 'El que fa falta per fer-la. No descompta del magatzem.',
        materialPlaceholder: 'Cerca al magatzem o escriu-ho',
        quantity: 'Quantitat',
        unit: 'Unitat',
        removeLine: 'Treure la línia',
        noMaterials: 'Res apuntat. Si cal material, posa\'l aquí i qui hi vagi ho veurà abans de sortir.',
        addMaterial: 'Afegir material',
        leaveTitle: 'Et deixes la tasca a mitges',
        leaveMessage: 'El que has escrit no s\'ha desat.',
        leaveConfirm: 'Sortir igualment',
        leaveCancel: 'Continuar aquí',
      },
    },

    assistant: {
      openers: {
        tasks: {
          'pending-here': 'Què hi ha pendent en un lloc?',
          'what-mine': 'Què tinc jo pendent?',
        },
      },
    },

    modules: { tasks: 'Tasques' },
    permissions: {
      tasks: {
        read: 'Veure les tasques i el que hi ha pendent a cada lloc',
        complete: 'Donar tasques per fetes i apuntar que no es van poder',
        write: 'Apuntar tasques, editar-les i anul·lar-les',
        import: "Importar tasques d'un altre programa",
      },
    },

    settings: {
      ns: {
        tasks: {
          title: 'Tasques',
          subtitle: 'Com anomeneu el que feu, si és que li poseu nom',
          alwaysRequireReport: {
            label: 'Les tasques noves es tanquen amb un comunicat',
            help: 'Encès, cada tasca nova neix esperant que algú ompli un comunicat — que és com queden apuntades les seves hores i el seu material. Apagat, es tanca amb un tic. En tots dos casos es pot canviar a la mateixa tasca.',
          },
          types: {
            label: 'Els tipus de tasca que feu',
            help: 'Vostres, no nostres, i opcional: si ho deixes buit una tasca és la frase que va escriure algú.',
          },
        },
      },
    },

    audit: {
      actions: {
        tasks: {
          task_created: 'Tasca apuntada',
          task_updated: 'Tasca modificada',
          task_done: 'Tasca donada per feta',
          task_skipped: 'Tasca que no es va poder fer',
          task_cancelled: 'Tasca anul·lada',
          task_pending: 'Tasca reoberta',
          task_in_progress: 'Tasca començada',
          task_noted: 'Nota en una tasca',
          task_attached: 'Fitxer adjuntat a una tasca',
          task_detached: 'Fitxer tret d\'una tasca',
          imported: "Tasca importada d'un altre programa",
        },
      },
    },
  },

  en: {
    tasks: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'one of your tasks is past its date | {count} of your tasks are past their date',
        open: 'one open task | {count} open tasks',
      },
      assistant: {
        // Las respuestas que contestan las reglas (queries.js) y la pregunta de
        // «completar una tarea» por voz (intents.js).
        query: {
          mine: {
            some: 'You have {count} open tasks: {list}.',
            none: 'You have no open tasks.',
          },
        },
        complete: {
          askTask: 'Which task?',
          action: 'Tick a task off',
          field: { task: 'Task', assignee: 'Was for', note: 'How it went' },
          where: 'At',
          after: 'It is closed in your name, with today\'s date',
          notes: {
            noTask: 'Which task? Say its code or what it says',
            report: 'This one is closed by filling in its work report, not by ticking it',
          },
          outcome: 'Task {code} done',
        },
        create: {
          summary: {
            plain: 'New task: {title}',
            at: 'New task at {subject}: {title}',
            for: 'New task for {assignee}: {title}',
            atfor: 'New task at {subject} for {assignee}: {title}',
          },
          action: 'Write a task down',
          field: {
            title: 'To do',
            description: 'Detail',
            assignee: 'For',
            due: 'Due',
            priority: 'Priority',
          },
          where: 'Task at',
          outcome: 'Task {code} created',
        },
      },
      title: 'Tasks',
      notifications: {
        categories: {
          assigned: 'A task is assigned to you',
          completed: 'A task you asked for is done',
        },
        assigned: {
          title: 'New task for you',
          body: '{code} · {title}{where}',
        },
        completed: {
          title: 'Task done',
          body: '{code} · {title}',
        },
      },
      subtitle: 'What is written down and nobody has done yet',

      help: {
        title: 'Tasks',
        subtitle: 'The two ways a task gets closed, why a task never falls due, and where a site’s outstanding work shows up',
      },
      detailTitle: 'Task',
      newTitle: 'New task',
      editTitle: 'Edit the task',
      late: 'Past its date',

      related: { pending: 'Outstanding here' },

      actions: {
        new: 'New task',
        complete: 'Mark as done',
        skip: 'Could not be done',
        edit: 'Edit',
        cancel: 'Cancel',
        reopen: 'Reopen',
      },

      status: {
        pending: 'Waiting',
        in_progress: 'In progress',
        done: 'Done',
        skipped: 'Could not',
        cancelled: 'Cancelled',
      },

      priority: { low: 'Low', medium: 'Normal', high: 'High', critical: 'Critical' },

      subjectType: {
        property: 'Property',
        asset: 'Asset',
        project: 'Project',
        vehicle: 'Vehicle',
        customer: 'Customer',
        location: 'Location',
        work_order: 'Work order',
        work: 'Job',
      },

      stats: {
        open: 'Waiting',
        overdue: 'Past their date',
        skipped: 'Could not be done',
        mine: 'Mine',
      },

      filters: {
        search: 'Search',
        searchPlaceholder: 'What it says, the code or where it is',
        status: 'State',
        statusAny: 'Any',
        type: 'Kind',
        typeAny: 'Any',
        priority: 'Priority',
        priorityAny: 'Any',
      },

      columns: {
        code: 'Code',
        task: 'Task',
        status: 'State',
        priority: 'Priority',
        waiting: 'Waiting',
      },

      waiting: {
        today: 'Today',
        days: '{days} days ago',
        months: '{months} months ago',
      },

      empty: {
        title: 'Nothing written down',
        hint: 'Write down what you see and it stays here until somebody does it.',
      },

      bulk: {
        title: 'What you ticked',
        chosen: '{n} ticked | {n} ticked | {n} ticked',
        clear: 'Clear',
        complete: 'Done ({n})',
        done: '{n} marked as done',
        failed: '{n} could not be done: they stay ticked',
      },

      pager: { previous: 'Previous', next: 'Next', page: 'Page {page} of {of}' },

      outcome: {
        done: 'Done by {who} on {when}',
        skipped: 'Could not be done on {when}',
        cancelled: 'Cancelled on {when}',
        somebody: 'somebody',
      },

      materials: {
        title: 'What to take along',
        noQuantity: 'no quantity',
        note: 'This is what is needed, not what was used: it moves no stock. What was really used is written on the work order\'s report.',
      },

      files: {
        title: 'Photos and papers',
        empty: 'Nothing attached yet',
        add: 'Attach a file',
        uploading: 'Uploading…',
        remove: 'Remove',
      },

      facts: {
        title: 'The file',
        subject: 'Where',
        assignee: 'Whose it is',
        nobody: 'Nobody yet',
        due: 'Due',
        noDue: 'No date',
        written: 'Written down on',
        origin: 'Origin',
        importedFrom: 'Imported from {system}',
        writtenThere: 'written there on {date}',
      },

      history: {
        title: 'History',
        created: 'Written down',
        updated: 'Edited',
        note: 'Note',
        add: 'Add',
        addPlaceholder: 'What happened…',
      },

      skip: {
        title: 'It could not be done',
        reason: 'Why',
        hint: 'This sentence is the only thing telling «we went and could not» apart from «nobody has been».',
        placeholder: 'The gate was locked…',
        cancel: 'Leave it',
        confirm: 'Write it down',
      },

      confirm: {
        complete: 'Mark it as done? It will carry your name and today\'s date.',
        cancelTitle: 'Cancel the task',
        cancelMessage: 'It stops waiting and is not deleted: it stays in the history with whoever wrote it.',
        detachTitle: 'Remove the file',
        detachMessage: '«{name}» will no longer be on this task.',
      },

      toast: {
        created: 'Task written down',
        saved: 'Task saved',
        completed: 'Marked as done',
        skipped: 'Written down that it could not be done',
        reopened: 'Waiting again',
        cancelled: 'Task cancelled',
        attached: 'Attached',
      },

      dictate: {
        title: 'Dictate it',
        subtitle: 'Record it and the form fills itself. Then you check it and save.',
        clip: 'The voice note',
        read: 'Fill in what I said',
        heard: 'What was heard',
        silence: 'Nothing was audible. Record it again, closer to your mouth.',
        which: '"{words}": there are several. Which one?',
        notFound: 'I could not find "{words}". Pick it by hand below.',
        keeps: 'The recording is attached to the task when you save it. Remove it with the × if you would rather not.',
        clipFailed: 'The task was saved, but the recording could not be attached.',
      },

      report: {
        withReport: 'With a report',
        withoutReport: 'No report',
        closedByReport: 'This task is closed by filling in a work report, not by ticking it.',
        closedByReportAt: 'This task is closed by filling in the report for {place}, not by ticking it.',
      },
      form: {
        subtitle: 'What has to be done, and where or whose it is',
        requiresReport: 'How it gets closed',
        requiresReportOn: 'It will be closed by filling in a report on that site’s work order. It cannot be ticked from here.',
        requiresReportOff: 'It is closed by ticking it, with a note if needed. Its hours and materials are not recorded anywhere.',
        what: 'What has to be done',
        who: 'Where and whose',
        title: 'The task',
        titleHint: 'One sentence. The one you would say on the phone.',
        titlePlaceholder: 'Change the sprinkler in zone 3',
        description: 'Details',
        type: 'Kind',
        typeAny: 'No kind',
        priority: 'Priority',
        subjectType: 'About what',
        subjectNone: 'Not about anything in particular',
        subject: 'Which one',
        subjectPlaceholder: 'Find the place, the machine or the job',
        assignee: 'Whose it is',
        assigneeHint: 'It can be nobody\'s: then it waits for whoever turns up.',
        assigneePlaceholder: 'Find the person',
        due: 'Due',
        dueHint: 'Almost always empty. A task at a place waits, it does not fall due.',
        unreachable: 'Give it a place or a person: otherwise it would show up on no screen at all.',
        save: 'Save',
        discard: 'Discard',
        materials: 'What to take along',
        materialsHint: 'What is needed to do it. It does not take anything off the shelf.',
        materialPlaceholder: 'Find it in the warehouse, or type it',
        quantity: 'Quantity',
        unit: 'Unit',
        removeLine: 'Remove the line',
        noMaterials: 'Nothing listed. If something is needed, put it here and whoever goes will see it before setting off.',
        addMaterial: 'Add material',
        leaveTitle: 'You are leaving the task half written',
        leaveMessage: 'What you typed has not been saved.',
        leaveConfirm: 'Leave anyway',
        leaveCancel: 'Stay here',
      },
    },

    assistant: {
      openers: {
        tasks: {
          'pending-here': 'What is outstanding at a place?',
          'what-mine': 'What have I got waiting?',
        },
      },
    },

    modules: { tasks: 'Tasks' },
    permissions: {
      tasks: {
        read: 'See the tasks and what is outstanding at each place',
        complete: 'Mark tasks as done and write down that one could not be',
        write: 'Write tasks down, edit them and cancel them',
        import: 'Import tasks from another program',
      },
    },

    settings: {
      ns: {
        tasks: {
          title: 'Tasks',
          subtitle: 'What you call the work you do, if you name it at all',
          alwaysRequireReport: {
            label: 'New tasks are closed with a work report',
            help: 'On, every new task is written expecting somebody to fill in a report — which is how its hours and materials get recorded. Off, it is closed with a tick. Either way it can be changed on the task itself.',
          },
          types: {
            label: 'The kinds of task you do',
            help: 'Yours, not ours, and optional: leave it empty and a task is the line somebody wrote.',
          },
        },
      },
    },

    audit: {
      actions: {
        tasks: {
          task_created: 'Task written down',
          task_updated: 'Task edited',
          task_done: 'Task marked as done',
          task_skipped: 'Task that could not be done',
          task_cancelled: 'Task cancelled',
          task_pending: 'Task reopened',
          task_in_progress: 'Task started',
          task_noted: 'Note on a task',
          task_attached: 'File attached to a task',
          task_detached: 'File removed from a task',
          imported: 'Task imported from another program',
        },
      },
    },
  },

  fr: {
    tasks: {
      // Los avisos de la mañana y de la tarjeta «Hoy» (backend/nudges.js).
      nudges: {
        overdue: 'une de tes tâches est en retard | {count} de tes tâches sont en retard',
        open: 'une tâche en attente | {count} tâches en attente',
      },
      assistant: {
        // Las respuestas que contestan las reglas (queries.js) y la pregunta de
        // «completar una tarea» por voz (intents.js).
        query: {
          mine: {
            some: 'Vous avez {count} tâches ouvertes : {list}.',
            none: 'Vous n’avez aucune tâche ouverte.',
          },
        },
        complete: {
          askTask: 'Quelle tâche ?',
          action: 'Marquer une tâche comme faite',
          field: { task: 'Tâche', assignee: 'Était pour', note: 'Comment ça s\'est passé' },
          where: 'À',
          after: 'Elle est clôturée à votre nom, à la date du jour',
          notes: {
            noTask: 'Quelle tâche ? Dites son code ou ce qu\'elle dit',
            report: 'Celle-ci se clôture en remplissant son rapport, pas en la cochant',
          },
          outcome: 'Tâche {code} faite',
        },
        create: {
          summary: {
            plain: 'Nouvelle tâche : {title}',
            at: 'Nouvelle tâche à {subject} : {title}',
            for: 'Nouvelle tâche pour {assignee} : {title}',
            atfor: 'Nouvelle tâche à {subject} pour {assignee} : {title}',
          },
          action: 'Créer une tâche',
          field: {
            title: 'À faire',
            description: 'Détail',
            assignee: 'Pour',
            due: 'Échéance',
            priority: 'Priorité',
          },
          where: 'Tâche à',
          outcome: 'Tâche {code} créée',
        },
      },
      title: 'Tâches',
      notifications: {
        categories: {
          assigned: 'Une tâche vous est attribuée',
          completed: 'Une tâche que vous avez demandée est faite',
        },
        assigned: {
          title: 'Nouvelle tâche pour vous',
          body: '{code} · {title}{where}',
        },
        completed: {
          title: 'Tâche terminée',
          body: '{code} · {title}',
        },
      },
      subtitle: 'Ce qui est noté et que personne n\'a encore fait',

      help: {
        title: 'Tâches',
        subtitle: 'Les deux façons de fermer une tâche, pourquoi une tâche n’échoit jamais, et où apparaît l’attente de chaque lieu',
      },
      detailTitle: 'Tâche',
      newTitle: 'Nouvelle tâche',
      editTitle: 'Modifier la tâche',
      late: 'Hors délai',

      related: { pending: 'En attente ici' },

      actions: {
        new: 'Nouvelle tâche',
        complete: 'Marquer comme faite',
        skip: 'N\'a pas pu être faite',
        edit: 'Modifier',
        cancel: 'Annuler',
        reopen: 'Rouvrir',
      },

      status: {
        pending: 'En attente',
        in_progress: 'En cours',
        done: 'Faite',
        skipped: 'N\'a pas pu',
        cancelled: 'Annulée',
      },

      priority: { low: 'Basse', medium: 'Normale', high: 'Haute', critical: 'Critique' },

      subjectType: {
        property: 'Emplacement',
        asset: 'Actif',
        project: 'Projet',
        vehicle: 'Véhicule',
        customer: 'Client',
        location: 'Emplacement de stock',
        work_order: 'Ordre de travail',
        work: 'Chantier',
      },

      stats: {
        open: 'En attente',
        overdue: 'Hors délai',
        skipped: 'N\'ont pas pu être faites',
        mine: 'Les miennes',
      },

      filters: {
        search: 'Rechercher',
        searchPlaceholder: 'Ce qui est écrit, le code ou l\'endroit',
        status: 'État',
        statusAny: 'Tous',
        type: 'Type',
        typeAny: 'Tous',
        priority: 'Priorité',
        priorityAny: 'Toutes',
      },

      columns: {
        code: 'Code',
        task: 'Tâche',
        status: 'État',
        priority: 'Priorité',
        waiting: 'En attente depuis',
      },

      waiting: {
        today: 'Aujourd\'hui',
        days: 'Il y a {days} jours',
        months: 'Il y a {months} mois',
      },

      empty: {
        title: 'Rien de noté',
        hint: 'Notez ce que vous voyez : ça reste ici jusqu\'à ce que quelqu\'un le fasse.',
      },

      bulk: {
        title: 'Ce que vous avez coché',
        chosen: '{n} cochées | {n} cochée | {n} cochées',
        clear: 'Tout décocher',
        complete: 'Fait ({n})',
        done: '{n} marquées comme faites',
        failed: '{n} n\u2019ont pas pu être faites : elles restent cochées',
      },

      pager: { previous: 'Précédent', next: 'Suivant', page: 'Page {page} sur {of}' },

      outcome: {
        done: 'Faite par {who} le {when}',
        skipped: 'N\'a pas pu être faite le {when}',
        cancelled: 'Annulée le {when}',
        somebody: 'quelqu\'un',
      },

      materials: {
        title: 'Ce qu\'il faut emporter',
        noQuantity: 'sans quantité',
        note: 'C\'est ce qu\'il faut, pas ce qui a été consommé : cela ne bouge pas le stock. Ce qui a été consommé se note sur le rapport de l\'ordre.',
      },

      files: {
        title: 'Photos et documents',
        empty: 'Rien de joint pour l\'instant',
        add: 'Joindre un fichier',
        uploading: 'Envoi…',
        remove: 'Retirer',
      },

      facts: {
        title: 'La fiche',
        subject: 'Où',
        assignee: 'À qui',
        nobody: 'À personne pour l\'instant',
        due: 'Pour quand',
        noDue: 'Sans date',
        written: 'Notée le',
        origin: 'Origine',
        importedFrom: 'Importée de {system}',
        writtenThere: 'créée là-bas le {date}',
      },

      history: {
        title: 'Historique',
        created: 'Notée',
        updated: 'Modifiée',
        note: 'Note',
        add: 'Ajouter',
        addPlaceholder: 'Ce qui s\'est passé…',
      },

      skip: {
        title: 'Ça n\'a pas pu être fait',
        reason: 'Pourquoi',
        hint: 'Cette phrase est la seule chose qui distingue « on y est allés et ça n\'a pas pu » de « personne n\'y est allé ».',
        placeholder: 'Le portail était fermé…',
        cancel: 'Laisser',
        confirm: 'Le noter',
      },

      confirm: {
        complete: 'Vous la marquez comme faite ? Elle portera votre nom et la date du jour.',
        cancelTitle: 'Annuler la tâche',
        cancelMessage: 'Elle cesse d\'être en attente et n\'est pas supprimée : elle reste dans l\'historique avec la personne qui l\'a notée.',
        detachTitle: 'Retirer le fichier',
        detachMessage: '« {name} » ne sera plus sur cette tâche.',
      },

      toast: {
        created: 'Tâche notée',
        saved: 'Tâche enregistrée',
        completed: 'Marquée comme faite',
        skipped: 'Noté qu\'elle n\'a pas pu être faite',
        reopened: 'De nouveau en attente',
        cancelled: 'Tâche annulée',
        attached: 'Joint',
      },

      dictate: {
        title: 'La dicter',
        subtitle: 'Enregistrez-la et le formulaire se remplit. Vous relisez, puis vous enregistrez.',
        clip: 'La note vocale',
        read: 'Remplir avec ce que j\'ai dit',
        heard: 'Ce qui a été entendu',
        silence: 'Rien n\'a été entendu. Réenregistrez, plus près de la bouche.',
        which: '« {words} » : il y en a plusieurs. Lequel ?',
        notFound: 'Je n\'ai pas trouvé « {words} ». Choisissez-le à la main ci-dessous.',
        keeps: 'L\'enregistrement reste attaché à la tâche une fois enregistrée. Retirez-le avec la × si vous ne le voulez pas.',
        clipFailed: 'La tâche est enregistrée, mais l\'enregistrement n\'a pas pu être attaché.',
      },

      report: {
        withReport: 'Avec rapport',
        withoutReport: 'Sans rapport',
        closedByReport: 'Cette tâche se ferme en remplissant un rapport de travail, pas en la cochant.',
        closedByReportAt: 'Cette tâche se ferme en remplissant le rapport de {place}, pas en la cochant.',
      },
      form: {
        subtitle: 'Ce qu\'il faut faire, et où ou à qui',
        requiresReport: 'Comment elle se ferme',
        requiresReportOn: 'Elle se fermera en remplissant un rapport sur l’ordre de travail du lieu. Impossible de la cocher d’ici.',
        requiresReportOff: 'Elle se ferme en la cochant, avec une note si besoin. Ses heures et son matériel ne sont consignés nulle part.',
        what: 'Ce qu\'il faut faire',
        who: 'Où et à qui',
        title: 'La tâche',
        titleHint: 'Une phrase. Celle que vous diriez au téléphone.',
        titlePlaceholder: 'Changer l\'arroseur de la zone 3',
        description: 'Détails',
        type: 'Type',
        typeAny: 'Sans type',
        priority: 'Priorité',
        subjectType: 'À propos de quoi',
        subjectNone: 'De rien en particulier',
        subject: 'Lequel',
        subjectPlaceholder: 'Cherchez le lieu, la machine ou le chantier',
        assignee: 'À qui',
        assigneeHint: 'Elle peut n\'être à personne : elle attend alors celui qui passera.',
        assigneePlaceholder: 'Cherchez la personne',
        due: 'Pour quand',
        dueHint: 'Presque toujours vide. Une tâche d\'un lieu attend, elle n\'échoit pas.',
        unreachable: 'Donnez-lui un lieu ou une personne : sinon elle n\'apparaîtrait sur aucun écran.',
        save: 'Enregistrer',
        discard: 'Abandonner',
        materials: 'Ce qu\'il faut emporter',
        materialsHint: 'Ce qu\'il faut pour la faire. Cela ne décompte pas du stock.',
        materialPlaceholder: 'Cherchez au magasin, ou saisissez-le',
        quantity: 'Quantité',
        unit: 'Unité',
        removeLine: 'Retirer la ligne',
        noMaterials: 'Rien de noté. S\'il faut du matériel, mettez-le ici : celui qui ira le verra avant de partir.',
        addMaterial: 'Ajouter du matériel',
        leaveTitle: 'Vous laissez la tâche à moitié écrite',
        leaveMessage: 'Ce que vous avez saisi n\'a pas été enregistré.',
        leaveConfirm: 'Sortir quand même',
        leaveCancel: 'Rester ici',
      },
    },

    assistant: {
      openers: {
        tasks: {
          'pending-here': 'Qu\'est-ce qui est en attente à un endroit ?',
          'what-mine': 'Qu\'est-ce que j\'ai en attente ?',
        },
      },
    },

    modules: { tasks: 'Tâches' },
    permissions: {
      tasks: {
        read: 'Voir les tâches et ce qui est en attente à chaque endroit',
        complete: 'Marquer des tâches comme faites et noter qu\'une n\'a pas pu l\'être',
        write: 'Noter des tâches, les modifier et les annuler',
        import: "Importer des tâches d'un autre programme",
      },
    },

    settings: {
      ns: {
        tasks: {
          title: 'Tâches',
          subtitle: 'Comment vous appelez ce que vous faites, si vous lui donnez un nom',
          alwaysRequireReport: {
            label: 'Les nouvelles tâches se ferment avec un rapport',
            help: 'Activé, chaque nouvelle tâche est écrite en attendant que quelqu’un remplisse un rapport — c’est ainsi que ses heures et son matériel sont consignés. Désactivé, elle se ferme d’une coche. Dans les deux cas, cela se change sur la tâche elle-même.',
          },
          types: {
            label: 'Les types de tâche que vous faites',
            help: 'Les vôtres, pas les nôtres, et facultatif : laissez vide et une tâche est la phrase que quelqu\'un a écrite.',
          },
        },
      },
    },

    audit: {
      actions: {
        tasks: {
          task_created: 'Tâche notée',
          task_updated: 'Tâche modifiée',
          task_done: 'Tâche marquée comme faite',
          task_skipped: 'Tâche qui n\'a pas pu être faite',
          task_cancelled: 'Tâche annulée',
          task_pending: 'Tâche rouverte',
          task_in_progress: 'Tâche commencée',
          task_noted: 'Note sur une tâche',
          task_attached: 'Fichier joint à une tâche',
          task_detached: 'Fichier retiré d\'une tâche',
          imported: "Tâche importée d'un autre programme",
        },
      },
    },
  },
}

// El vocabulario de la analítica se mezcla aparte: es un CONTRATO con
// `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'tasks')))
